import React, { useEffect, useRef, useState } from "react";
import { apiService } from "../../services/apiService";
import { GoogleDriveAttachment } from "../../types";
import { GoogleDriveFilePicker } from "./GoogleDriveFilePicker";
import ProjectFolderSelectModal from "./ProjectFolderSelectModal";
import { useTranslation } from "react-i18next";
import toast from "../../utils/toast";
import {
  XMarkIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  CloudArrowUpIcon,
  ArrowLeftIcon,
  PlusIcon,
  FolderIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

interface WorkflowInstanceItem {
  id: number;
  name: string;
  projectName: string;
  description?: string;
  workflowId?: number;
  workflowContainerName?: string;
  templateWorkflowName?: string;
}

interface UploadFilesToProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userId: number;
  initialProjectId?: number | null;
  initialProjectName?: string;
}

type LocalUploadFile = File & {
  webkitRelativePath?: string;
};

type FolderPathEntry = {
  id: string | null;
  name: string;
};

type SelectedLocalFile = {
  id: string;
  file: LocalUploadFile;
  folderId: string | null;
  folderPath: string;
};

type SelectedDriveAttachment = GoogleDriveAttachment & {
  targetFolderId: string | null;
  targetFolderPath: string;
};

const UploadFilesToProjectModal: React.FC<UploadFilesToProjectModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userId,
  initialProjectId,
  initialProjectName,
}) => {
  const { t } = useTranslation([
    "activities",
    "errors",
    "common",
    "projects",
    "dashboard",
  ]);
  const projectRootLabel = t("projects:projectFilesModal.rootFolder");

  const [step, setStep] = useState(1);
  const [projects, setProjects] = useState<WorkflowInstanceItem[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [selectedProjectName, setSelectedProjectName] = useState<string>("");

  // File upload state
  const [selectedFiles, setSelectedFiles] = useState<SelectedLocalFile[]>([]);
  const [driveAttachments, setDriveAttachments] = useState<
    SelectedDriveAttachment[]
  >([]);
  const [fileDescriptions, setFileDescriptions] = useState<Record<string, string>>({});
  const [showDrivePicker, setShowDrivePicker] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedFolderPath, setSelectedFolderPath] = useState<string>(
    projectRootLabel
  );
  const [selectedFolderPathEntries, setSelectedFolderPathEntries] = useState<
    FolderPathEntry[]
  >([{ id: null, name: projectRootLabel }]);
  const [showFolderSelectModal, setShowFolderSelectModal] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const isProjectLocked = Boolean(initialProjectId);
  const showBackButton = step > 1 && !isProjectLocked;
  const selectedProjectRootLabel = selectedProjectName || projectRootLabel;
  const buildRootPathEntries = (rootLabel: string): FolderPathEntry[] => [
    { id: null, name: rootLabel },
  ];
  const createLocalFileId = (file: File): string =>
    `${file.name}-${file.size}-${file.lastModified}-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
  const normalizeSearchText = (value?: string | null): string =>
    String(value || "")
      .toLocaleLowerCase("tr-TR")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\u0131/g, "i")
      .trim();
  const getLocalFileRelativeFolderPath = (file: LocalUploadFile): string => {
    const relativePath = file.webkitRelativePath
      ?.replace(/\\/g, "/")
      .trim();
    if (!relativePath) {
      return "";
    }
    const segments = relativePath.split("/").filter(Boolean);
    segments.pop();
    return segments.join("/");
  };
  const getLocalFileRootFolderName = (file: LocalUploadFile): string => {
    const relativePath = file.webkitRelativePath
      ?.replace(/\\/g, "/")
      .trim();
    if (!relativePath) {
      return "";
    }
    return relativePath.split("/").filter(Boolean)[0] || "";
  };
  const getLocalFileDisplayPath = (file: LocalUploadFile): string => {
    const relativePath = file.webkitRelativePath
      ?.replace(/\\/g, "/")
      .trim();
    return relativePath || file.name;
  };
  const getFileTargetFolderDisplayPath = (selectedFile: SelectedLocalFile): string => {
    const basePath = selectedFile.folderPath || selectedProjectRootLabel;
    const relativeFolderPath = getLocalFileRelativeFolderPath(selectedFile.file);
    if (!relativeFolderPath) {
      return basePath;
    }
    return `${basePath} / ${relativeFolderPath
      .split("/")
      .filter(Boolean)
      .join(" / ")}`;
  };
  const normalizeFolderName = (value?: string | null): string =>
    String(value || "").trim().toLocaleLowerCase("tr-TR");
  const buildUniqueFolderName = (
    baseName: string,
    existingFolderNames: Set<string>
  ): string => {
    const trimmedBaseName = baseName.trim() || "Klasör";
    let candidate = trimmedBaseName;
    let suffix = 2;

    while (existingFolderNames.has(normalizeFolderName(candidate))) {
      candidate = `${trimmedBaseName} (${suffix})`;
      suffix += 1;
    }

    existingFolderNames.add(normalizeFolderName(candidate));
    return candidate;
  };

  const buildSelectedLocalFiles = (files: File[]): SelectedLocalFile[] =>
    files.map((file) => ({
      id: createLocalFileId(file),
      file: file as LocalUploadFile,
      folderId: selectedFolderId,
      folderPath: selectedFolderPath || selectedProjectRootLabel,
    }));

  const buildSelectedDriveAttachments = (
    attachments: GoogleDriveAttachment[]
  ): SelectedDriveAttachment[] =>
    attachments.map((attachment) => ({
      ...attachment,
      targetFolderId: selectedFolderId,
      targetFolderPath: selectedFolderPath || selectedProjectRootLabel,
    }));

  useEffect(() => {
    if (isOpen) {
      if (initialProjectId) {
        setSelectedProjectId(initialProjectId);
        setSelectedProjectName(initialProjectName || "");
        setStep(2);
        setSelectedFolderId(null);
        setSelectedFolderPath(initialProjectName || projectRootLabel);
        setSelectedFolderPathEntries(
          buildRootPathEntries(initialProjectName || projectRootLabel)
        );
        setShowFolderSelectModal(false);
      } else {
        loadProjects();
      }
    } else {
      // Reset state when modal closes
      setStep(1);
      setSelectedProjectId(null);
      setSelectedProjectName("");
      setSelectedFiles([]);
      setDriveAttachments([]);
      setFileDescriptions({});
      setSelectedFolderId(null);
      setSelectedFolderPath(projectRootLabel);
      setSelectedFolderPathEntries(buildRootPathEntries(projectRootLabel));
      setShowFolderSelectModal(false);
      setSearchTerm("");
      setError(null);
    }
  }, [isOpen, initialProjectId, initialProjectName, projectRootLabel]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const projectsData = await apiService.getUserWorkflowInstances(userId);
      setProjects(projectsData);
    } catch (err) {
      setError(t("errors:loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSelect = (projectId: number, projectName: string) => {
    setSelectedProjectId(projectId);
    setSelectedProjectName(projectName);
    setSelectedFolderId(null);
    setSelectedFolderPath(projectName || projectRootLabel);
    setSelectedFolderPathEntries(
      buildRootPathEntries(projectName || projectRootLabel)
    );
    setStep(2);
  };

  const addFiles = (files: FileList | File[]) => {
    if (!files || files.length === 0) return;
    const normalizedFiles = Array.from(files);
    if (normalizedFiles.length === 0) return;
    setSelectedFiles((prev) => [...prev, ...buildSelectedLocalFiles(normalizedFiles)]);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const normalizedFiles = Array.from(files);
      event.target.value = "";
      addFiles(normalizedFiles);
    }
  };

  const handleFolderSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const normalizedFiles = Array.from(files);
      event.target.value = "";
      addFiles(normalizedFiles);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!uploadingFiles) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
    if (uploadingFiles) return;
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      addFiles(files);
      event.dataTransfer.clearData();
    }
  };

  const removeFile = (fileId: string) => {
    setSelectedFiles((prev) => prev.filter((file) => file.id !== fileId));
    setFileDescriptions((prev) => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
  };

  const removeDriveFile = (fileId: string) => {
    setDriveAttachments((prev) => prev.filter((file) => file.id !== fileId));
    setFileDescriptions((prev) => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
  };

  const resolveUploadFolderIdForFile = async (
    workflowInstanceId: number,
    selectedFile: SelectedLocalFile,
    folderCache: Map<string, string | null>,
    rootFolderNameCache: Map<string, string>
  ): Promise<string | null> => {
    const relativeFolderPath = getLocalFileRelativeFolderPath(selectedFile.file);
    if (!relativeFolderPath) {
      return selectedFile.folderId;
    }

    const segments = relativeFolderPath.split("/").filter(Boolean);
    const baseFolderKey = selectedFile.folderId ?? "__root__";
    let currentParentId = selectedFile.folderId ?? null;
    let currentPath = "";

    for (const segment of segments) {
      currentPath = currentPath ? `${currentPath}/${segment}` : segment;
      const cacheKey = `${baseFolderKey}::${currentPath}`;
      if (folderCache.has(cacheKey)) {
        currentParentId = folderCache.get(cacheKey) ?? null;
        continue;
      }

      let folderNameToCreate = segment;
      if (!currentPath.includes("/")) {
        const rootFolderName = getLocalFileRootFolderName(selectedFile.file);
        if (rootFolderName) {
          const rootFolderCacheKey = `${baseFolderKey}::${normalizeFolderName(
            rootFolderName
          )}`;
          if (rootFolderNameCache.has(rootFolderCacheKey)) {
            folderNameToCreate =
              rootFolderNameCache.get(rootFolderCacheKey) || segment;
          } else {
            const existingItems = await apiService.getWorkflowInstanceFiles(
              workflowInstanceId,
              currentParentId
            );
            const existingFolderNames = new Set(
              existingItems
                .filter(
                  (existingFile) =>
                    existingFile.mimeType ===
                    "application/vnd.google-apps.folder"
                )
                .map((existingFolder) => normalizeFolderName(existingFolder.name))
            );
            folderNameToCreate = buildUniqueFolderName(
              segment,
              existingFolderNames
            );
            rootFolderNameCache.set(rootFolderCacheKey, folderNameToCreate);
          }
        }
      }

      const createdFolder = await apiService.createProjectFolder(
        workflowInstanceId,
        folderNameToCreate,
        currentParentId
      );
      currentParentId = createdFolder.id;
      folderCache.set(cacheKey, currentParentId);
    }

    return currentParentId;
  };

  const handleUpload = async () => {
    if (!selectedProjectId) {
      setError(t("errors:selectProject"));
      return;
    }

    if (selectedFiles.length === 0 && driveAttachments.length === 0) {
      setError(t("dashboard:uploadProjectFiles.emptyFilesError"));
      return;
    }

    try {
      setUploadingFiles(true);
      setError(null);

      // Upload local files
      const uploadedLocal: GoogleDriveAttachment[] = [];
      const folderCache = new Map<string, string | null>();
      const rootFolderNameCache = new Map<string, string>();
      for (const selectedFile of selectedFiles) {
        try {
          const description = fileDescriptions[selectedFile.id];
          const targetFolderId = await resolveUploadFolderIdForFile(
            selectedProjectId,
            selectedFile,
            folderCache,
            rootFolderNameCache
          );
          const uploaded = await apiService.uploadFileToProject(
            selectedProjectId,
            selectedFile.file,
            description,
            undefined,
            undefined,
            targetFolderId
          );
          uploadedLocal.push(uploaded);
        } catch (err) {
          throw new Error(
            t("dashboard:uploadProjectFiles.fileUploadItemFailed", {
              name: selectedFile.file.name,
            })
          );
        }
      }

      // Copy Google Drive files to project folder
      const copiedDrive: GoogleDriveAttachment[] = [];
      for (const attachment of driveAttachments) {
        try {
          const description = fileDescriptions[attachment.id];
          const copied = await apiService.copyDriveFileToProject(
            selectedProjectId,
            attachment.id,
            description,
            attachment.targetFolderId
          );
          copiedDrive.push(copied);
        } catch (err) {
          throw new Error(
            t("dashboard:uploadProjectFiles.driveCopyItemFailed", {
              name: attachment.name,
            })
          );
        }
      }

      // Success
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || t("errors:uploadFailed"));
    } finally {
      setUploadingFiles(false);
    }
  };

  const filteredProjects = projects.filter((project) => {
    const searchLower = normalizeSearchText(searchTerm);
    const projectName = project.projectName || project.name || "";
    const workflowName = project.templateWorkflowName || "";
    return (
      normalizeSearchText(projectName).includes(searchLower) ||
      normalizeSearchText(workflowName).includes(searchLower)
    );
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-300">
        <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0 flex items-center gap-3">
              {step > 1 && !isProjectLocked && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="text-white/80 hover:text-white hover:bg-white/20 p-1.5 rounded-md transition-all"
                  disabled={uploadingFiles}
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                </button>
              )}
              <h2 className="text-base sm:text-lg font-semibold text-white truncate">
                {t("dashboard:uploadProjectFiles.title")}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 text-[11px] text-blue-100/80">
                <span
                  className={`px-2 py-0.5 rounded-full ${
                    step >= 1 ? "bg-white/20 text-white" : "opacity-60"
                  }`}
                >
                  {t("common:project")}
                </span>
                <span
                  className={`px-2 py-0.5 rounded-full ${
                    step >= 2 ? "bg-white/20 text-white" : "opacity-60"
                  }`}
                >
                  {t("dashboard:attachFiles")}
                </span>
              </div>
              <span className="text-[11px] text-blue-100/90">{step}/2</span>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-full transition-all duration-200"
                disabled={uploadingFiles}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)]">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-3">
              <span className="font-medium">{error}</span>
            </div>
          )}

          {/* Step 1: Select Project */}
          {step === 1 && (
            <div className="animate-in fade-in slide-in-from-right-5 duration-300">
              {/* Search */}
              <div className="mb-6">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-blue-400" />
                  <input
                    type="text"
                    placeholder={t("dashboard:searchProjects")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-blue-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-400 transition-all"
                  />
                </div>
              </div>

              {/* Projects List */}
              <div className="mb-6 max-h-[700px] overflow-y-auto border border-gray-200 rounded-lg">
                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : filteredProjects.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p className="text-sm">{t("dashboard:noProjectsFound")}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {filteredProjects.map((project) => (
                      <button
                        key={project.id}
                        onClick={() =>
                          handleProjectSelect(
                            project.id,
                            project.projectName || project.name
                          )
                        }
                        className={`w-full px-4 py-2.5 transition-all duration-200 text-left hover:bg-blue-50 ${
                          selectedProjectId === project.id
                            ? "bg-blue-50"
                            : "bg-white"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <h4 className="font-semibold text-gray-900 text-sm truncate flex-1 min-w-0">
                            {project.projectName}
                          </h4>
                          {project.templateWorkflowName && (
                            <p className="text-xs text-gray-500 truncate max-w-[40%]">
                              {project.templateWorkflowName}
                            </p>
                          )}
                          {selectedProjectId === project.id && (
                            <CheckIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Upload Files */}
          {step === 2 && (
            <div className="animate-in fade-in slide-in-from-right-5 duration-300">
              <div className="mb-4 text-xs text-gray-600 bg-gray-100 px-3 py-1 rounded-full inline-flex">
                {t("common:project")}: {selectedProjectName}
              </div>

              {/* File Upload Section */}
              <div className="space-y-6">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-gray-800 font-semibold">
                      {t("dashboard:attachFiles")}
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => folderInputRef.current?.click()}
                        className="flex items-center space-x-2 text-sm text-amber-700 hover:text-amber-800 font-medium bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded-lg transition-colors"
                        disabled={uploadingFiles}
                      >
                        <FolderIcon className="w-4 h-4" />
                        <span>Bilgisayardan Klasör Ekle</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowDrivePicker(true)}
                        className="flex items-center space-x-2 text-sm text-green-600 hover:text-green-700 font-medium bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-lg transition-colors"
                        disabled={uploadingFiles}
                      >
                        <PlusIcon className="w-4 h-4" />
                        <span>{t("dashboard:uploadProjectFiles.addFromDrive")}</span>
                      </button>
                    </div>
                  </div>

                  <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {t("dashboard:uploadProjectFiles.targetFolder")}
                      </p>
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {selectedFolderPath}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Bilgisayardan klasör seçerseniz seçilen klasör adı da bu
                      yolun altında oluşturulur.
                    </p>
                  </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setShowFolderSelectModal(true)}
                        className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        disabled={!selectedProjectId || uploadingFiles}
                      >
                        {t("dashboard:uploadProjectFiles.selectFolder")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedFolderId(null);
                          setSelectedFolderPath(selectedProjectRootLabel);
                          setSelectedFolderPathEntries(
                            buildRootPathEntries(selectedProjectRootLabel)
                          );
                        }}
                        className="px-3 py-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        disabled={uploadingFiles}
                        title={t("dashboard:uploadProjectFiles.backToRootFolder")}
                      >
                        {t("common:reset")}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-center w-full">
                    <div
                      role="button"
                      tabIndex={uploadingFiles ? -1 : 0}
                      onClick={() => {
                        if (!uploadingFiles) {
                          fileInputRef.current?.click();
                        }
                      }}
                      onKeyDown={(event) => {
                        if (uploadingFiles) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          fileInputRef.current?.click();
                        }
                      }}
                      className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                        uploadingFiles
                          ? "border-gray-300 bg-gray-50 cursor-not-allowed"
                          : isDragActive
                          ? "border-green-500 bg-green-100"
                          : "border-green-300 bg-green-50 hover:bg-green-100"
                      }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <svg
                          className="w-8 h-8 mb-3 text-green-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        <p className="mb-2 text-sm text-gray-500">
                          <span className="font-semibold">
                            {t("dashboard:clickToUpload")}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500">
                          {t("dashboard:uploadFileTypes")}
                        </p>
                      </div>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      multiple
                      onChange={handleFileSelect}
                      disabled={uploadingFiles}
                    />
                    <input
                      ref={folderInputRef}
                      type="file"
                      className="hidden"
                      multiple
                      onChange={handleFolderSelect}
                      disabled={uploadingFiles}
                      {...({ webkitdirectory: "", directory: "" } as any)}
                    />
                  </div>
                </div>

                {/* Selected Files List */}
                {(selectedFiles.length > 0 || driveAttachments.length > 0) && (
                  <div className="mt-4 space-y-2">
                    <h4 className="text-sm font-medium text-gray-700 flex items-center">
                      <span>
                        {t("dashboard:selectedFiles")} (
                        {selectedFiles.length + driveAttachments.length})
                      </span>
                    </h4>
                    <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                      {selectedFiles.map((selectedFile) => (
                        <div
                          key={selectedFile.id}
                          className="bg-white p-3 rounded-lg border border-gray-200 space-y-2"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <DocumentTextIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {selectedFile.file.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {(selectedFile.file.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                                {getLocalFileDisplayPath(selectedFile.file) !==
                                  selectedFile.file.name && (
                                  <p className="text-[11px] text-gray-400 truncate">
                                    {getLocalFileDisplayPath(selectedFile.file)}
                                  </p>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => removeFile(selectedFile.id)}
                              className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-all flex-shrink-0"
                              disabled={uploadingFiles}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                            <div className="min-w-0">
                              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                Dosya Klasörü
                              </p>
                              <p className="text-xs text-gray-700 truncate">
                                {getFileTargetFolderDisplayPath(selectedFile)}
                              </p>
                            </div>
                            <FolderIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          </div>
                          <input
                            type="text"
                            placeholder={t(
                              "dashboard:uploadProjectFiles.optionalDescription"
                            )}
                            value={fileDescriptions[selectedFile.id] || ""}
                            onChange={(e) =>
                              setFileDescriptions((prev) => ({
                                ...prev,
                                [selectedFile.id]: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={uploadingFiles}
                          />
                        </div>
                      ))}
                      {driveAttachments.map((att) => (
                        <div
                          key={`drive-${att.id}`}
                          className="bg-white p-3 rounded-lg border border-blue-100 space-y-2"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              {att.iconUrl ? (
                                <img
                                  src={att.iconUrl}
                                  alt=""
                                  className="w-5 h-5 flex-shrink-0"
                                />
                              ) : (
                                <DocumentTextIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const previewWindow =
                                      apiService.shouldOpenPreviewWindow(att.name, att.mimeType)
                                        ? window.open("", "_blank")
                                        : null;
                                    if (previewWindow) {
                                      previewWindow.opener = null;
                                      apiService.preparePreviewWindow(previewWindow, att.name);
                                    }
                                    void apiService
                                      .previewGoogleDriveFile(att.id, att.name, previewWindow)
                                      .catch(() => toast.error("Dosya görüntülenemedi."));
                                  }}
                                  className="text-sm font-medium text-blue-600 hover:underline truncate block text-left"
                                >
                                  {att.name}
                                </button>
                                <p className="text-xs text-gray-500">
                                  {t("dashboard:uploadProjectFiles.driveFile")}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => removeDriveFile(att.id)}
                              className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-all flex-shrink-0"
                              disabled={uploadingFiles}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                            <div className="min-w-0">
                              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                Dosya Klasörü
                              </p>
                              <p className="text-xs text-gray-700 truncate">
                                {att.targetFolderPath || selectedProjectRootLabel}
                              </p>
                            </div>
                            <FolderIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          </div>
                          <input
                            type="text"
                            placeholder={t(
                              "dashboard:uploadProjectFiles.optionalDescription"
                            )}
                            value={fileDescriptions[att.id] || ""}
                            onChange={(e) =>
                              setFileDescriptions((prev) => ({
                                ...prev,
                                [att.id]: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={uploadingFiles}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Google Drive Picker */}
              <GoogleDriveFilePicker
                isOpen={showDrivePicker}
                onClose={() => setShowDrivePicker(false)}
                onSelect={(files) => {
                  setDriveAttachments((prev) => [
                    ...prev,
                    ...buildSelectedDriveAttachments(files),
                  ]);
                  setShowDrivePicker(false);
                }}
              />

              <ProjectFolderSelectModal
                isOpen={showFolderSelectModal}
                onClose={() => setShowFolderSelectModal(false)}
                workflowInstanceId={selectedProjectId}
                initialFolderId={selectedFolderId}
                initialPath={selectedFolderPathEntries}
                rootLabel={selectedProjectRootLabel}
                onSelect={(folderId, path) => {
                  setSelectedFolderId(folderId);
                  setSelectedFolderPathEntries(
                    path || buildRootPathEntries(selectedProjectRootLabel)
                  );
                  const label = (path || [])
                    .map((p) => p.name)
                    .filter(Boolean)
                    .join(" / ");
                  setSelectedFolderPath(label || selectedProjectRootLabel);
                }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-8 py-4 border-t border-gray-200">
          <div
            className={`flex items-center ${
              showBackButton ? "justify-between" : "justify-end"
            }`}
          >
            {/* Back Button */}
            {showBackButton && (
              <button
                onClick={() => setStep(step - 1)}
                className="flex items-center justify-center space-x-2 px-5 py-2.5 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl transition-all font-medium disabled:opacity-50"
                disabled={uploadingFiles}
              >
                <ArrowLeftIcon className="w-4 h-4" />
                <span>{t("common:back")}</span>
              </button>
            )}

            {/* Submit Button (only on step 2) */}
            {step === 2 && (
              <button
                onClick={handleUpload}
                disabled={
                  uploadingFiles ||
                  (selectedFiles.length === 0 && driveAttachments.length === 0)
                }
                className="flex items-center justify-center space-x-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
              >
                {uploadingFiles ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{t("dashboard:uploading")}</span>
                  </>
                ) : (
                  <>
                    <CloudArrowUpIcon className="w-5 h-5" />
                    <span>{t("common:upload")}</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadFilesToProjectModal;
